/**
 * Fetch AI Assistant (LobeChat) SSO URL from Control Plane.
 *
 * Mirrors fetchChatwootSso.ts / fetchGhostSso.ts exactly.
 * GOTCHA: Control Plane JWT auth for allow_guest endpoints requires:
 *   1. NO Authorization header (Frappe rejects unknown Bearer tokens)
 *   2. access_token passed as body parameter
 *
 * The returned `url` (https://ai.zaviago.com/sso?token=xxx) is set as the iframe
 * src. The ai.zaviago.com shim validates the one-time token via
 * control_plane.api.sso.validate_sso_token, sets a per-user session cookie
 * (carrying the tenant derived from the JWT — never hardcoded), and redirects
 * to /chat. That tenant scopes every ERP tool call.
 */

import { getCPToken } from "@/lib/api/controlPlaneClient";

const CONTROL_PLANE_URL =
  import.meta.env.VITE_CONTROL_PLANE_URL || "https://control.zaviago.com";

export interface AiSsoResponse {
  success: boolean;
  sso_token: string;
  url: string;
  expires_in: number;
}

export async function fetchAiSso(
  tenantName: string,
  userEmail: string
): Promise<AiSsoResponse> {
  const token = getCPToken();
  if (!token) {
    throw new Error("Not authenticated to Control Plane");
  }

  const response = await fetch(
    `${CONTROL_PLANE_URL}/api/method/control_plane.api.ai.get_sso_url`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "omit",
      body: JSON.stringify({
        access_token: token,
        tenant_name: tenantName,
        email: userEmail,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    let message = "Failed to get AI SSO URL";
    if (error._server_messages) {
      try {
        const msgs = JSON.parse(error._server_messages);
        message =
          typeof msgs[0] === "string" ? msgs[0] : JSON.stringify(msgs[0]);
      } catch {
        message = String(error._server_messages);
      }
    } else if (error.message) {
      message = error.message;
    } else if (error.exc_type) {
      message = `${error.exc_type}: ${error.exc || "Unknown error"}`;
    }
    throw new Error(message);
  }

  const data = await response.json();
  return data.message as AiSsoResponse;
}
